import { Component, type ErrorInfo, type ReactNode } from "react";
import { ErrorPage } from "@/components/ui/error-page";
import type { ErrorPageSource } from "@/i18n/problem-detail";

/**
 * Route-level error boundary (ALL-4857). An unexpected render-tree crash has
 * no backend-issued reasonCode/supportReference, so it maps to the generic
 * `UNKNOWN_CLIENT_ERROR` category with no reference. D636 has exactly one
 * server-minted reference per request, so the page does not invent a
 * client-side one; the error code, time and page stay copyable through
 * "Copy details".
 */
function clientErrorSource(detail?: string): ErrorPageSource {
  return {
    reasonCode: "UNKNOWN_CLIENT_ERROR",
    supportReference: null,
    httpStatus: 500,
    detail,
    path: typeof window !== "undefined" ? window.location.pathname : undefined,
    timestamp: new Date().toISOString(),
  };
}

export class RouteErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean; source: ErrorPageSource | null }> {
  state: { failed: boolean; source: ErrorPageSource | null } = { failed: false, source: null };

  static getDerivedStateFromError(error: unknown) {
    // The source is built here rather than in componentDidCatch, because React
    // re-renders the boundary as soon as this returns, and componentDidCatch
    // only runs after that render commits. A `failed: true` state with a null
    // source re-rendered the still-crashing child, which threw again and
    // escaped past this boundary to the root — unmounting the whole tree and
    // leaving a blank page instead of this error page.
    //
    // React hands this whatever the child threw, and `throw null`,
    // `throw undefined` or a bare string are all legal JavaScript (a rejected
    // promise value passed through, a throw in third-party code). Reading
    // `.message` off one of those threw *inside* this error path, so that
    // failure escaped past the boundary to the root and blanked the page —
    // the same outcome, by a different route. Only an Error carries a message.
    const detail = error instanceof Error ? error.message : undefined;
    return { failed: true, source: clientErrorSource(detail) };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("Route render failed", error, info.componentStack);
  }

  render() {
    if (!this.state.failed) return this.props.children;
    // `failed` is only ever set together with a source (see
    // getDerivedStateFromError), so this default never takes effect; it exists
    // so the fallback always has a non-null source to render.
    return <ErrorPage source={this.state.source ?? clientErrorSource()} />;
  }
}
